/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { WEEK_DAYS } from '@sportninja/common/constants/weekDays'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { font, paragraphSmall } from 'src/components/css'
import {
  detailSectionStyle,
  detailSectionTitleStyle,
} from './detailSectionStyles'

export interface CandidateAvailableDaysProps {
  availableDays?: string[] | null
}

// Candidate payloads use lowercase day names ('tuesday') while WEEK_DAYS.value
// is capitalized — match case-insensitively, same as utils/candidateFormat.
const findDay = (day: string) =>
  WEEK_DAYS.find((w) => w.value.toLowerCase() === day.toLowerCase())

/** Available days as week-ordered chips. Renders nothing when empty. */
const CandidateAvailableDays: React.FC<CandidateAvailableDaysProps> = ({
  availableDays,
}) => {
  const days = availableDays ?? []
  if (days.length === 0) {
    return null
  }
  const ordered = [...days].sort(
    (a, b) => (findDay(a)?.index ?? 0) - (findDay(b)?.index ?? 0)
  )

  return (
    <div css={detailSectionStyle}>
      <span css={detailSectionTitleStyle}>
        {capitalizeWords('Available Days')}
      </span>
      <div css={chipsStyle}>
        {ordered.map((day) => (
          <span key={day} css={chipStyle}>
            {findDay(day)?.short ?? day}
          </span>
        ))}
      </div>
    </div>
  )
}

export default CandidateAvailableDays

const chipsStyle = css`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

/* Dark fill (matching the section panels) so the chips read as inset tokens
   rather than outlined buttons on the modal body. */
const chipStyle = css`
  ${font.body}
  ${paragraphSmall}
  padding: 4px 12px;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
`
